import { Injectable, inject } from "@angular/core";
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit as fsLimit,
  startAfter,
  QueryConstraint,
  Timestamp,
  Firestore,
} from "@angular/fire/firestore";
import { FirestoreService } from "./firestore.service";
import { Client, COL } from "./types";

export interface CreateClientInput {
  // Business profile
  companyName: string;
  agent?: string;
  category?: string;
  additionalInfo?: string;

  // Identity (flat)
  name?: string;
  lastname?: string;
  birthdate?: Date | null;
  documentId?: string;
  personType?: number | null;

  // Contact (flat)
  description_address?: string;
  phoneNumber?: string;
  email?: string;
}

export interface FindClientsParams {
  /** @deprecated Ignored — firm staff share one client pool (see Firestore rules). */
  creatorUid?: string;
  criteria?: string;         // case-insensitive prefix match against companyName
  category?: string;         // exact match unless "TODAS"
  limit?: number;
  /**
   * Cursor for pagination: pass the value of the field used in `orderBy`
   * (`companyName` or `companyNameLower`) of the last visible row.
   */
  startAfter?: string | null;
}

/**
 * ClientsService — CRUD over `clients/`. Documents are stored flat (no
 * embedded Person/Address/Contact bag).
 */
@Injectable({ providedIn: "root" })
export class ClientsService {
  private readonly fs = inject(FirestoreService);
  private readonly db = inject(Firestore);

  /** Find clients with optional filters. Pure read — no live subscription. */
  async find(params: FindClientsParams): Promise<Client[]> {
    const { creatorUid, criteria, category, limit = 12 } = params;
    const colRef = this.fs.col<Client>(COL.clients);

    const constraints: QueryConstraint[] = [];
    if (creatorUid) constraints.push(where("creatorUid", "==", creatorUid));

    if (category && category !== "TODAS") {
      constraints.push(where("category", "==", category));
    }

    const isPrefixSearch = !!criteria && criteria.trim().length > 0;
    if (isPrefixSearch) {
      const lower = criteria!.trim().toLowerCase();
      constraints.push(where("companyNameLower", ">=", lower));
      constraints.push(where("companyNameLower", "<", lower + "\uf8ff"));
      constraints.push(orderBy("companyNameLower", "asc"));
    } else {
      constraints.push(orderBy("companyName", "asc"));
    }

    if (params.startAfter) {
      // The cursor must match the ORDER BY field. When searching by a
      // prefix we order by the lowercase column, so normalize the cursor.
      const cursor = isPrefixSearch
        ? params.startAfter.toLowerCase()
        : params.startAfter;
      constraints.push(startAfter(cursor));
    }

    constraints.push(fsLimit(limit));

    const snap = await getDocs(query(colRef, ...constraints));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Client));
  }

  /**
   * Returns the first client whose `documentId` matches. Document IDs are
   * always normalised to a string of digits, both at write and at read time,
   * so legacy callers that pass a number still find the right doc.
   *
   * @param opts.creatorUid — Optional legacy filter; omit to search firm-wide.
   */
  async findByDocumentId(
    documentId: string | number,
    opts?: { creatorUid?: string }
  ): Promise<Client | null> {
    const normalised = String(documentId ?? "").replace(/\D/g, "");
    if (!normalised) return null;
    const colRef = this.fs.col<Client>(COL.clients);
    const constraints: QueryConstraint[] = [
      where("documentId", "==", normalised),
    ];
    if (opts?.creatorUid) {
      constraints.push(where("creatorUid", "==", opts.creatorUid));
    }
    constraints.push(fsLimit(1));
    const snap = await getDocs(query(colRef, ...constraints));
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() } as Client;
  }

  async getById(id: string): Promise<Client | null> {
    const ref = this.fs.docRef<Client>(COL.clients, id);
    const snap = await getDoc(ref);
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as Client) : null;
  }

  /**
   * Creates or upserts a client. Performs at most ONE read (only when we
   * don't know if the doc already exists, i.e. on edits with a stable id).
   * Returns the client we just wrote — built locally instead of re-reading.
   */
  async upsert(
    input: CreateClientInput & { id?: string },
    creator: { uid: string; name: string }
  ): Promise<Client> {
    const isExistingDoc = !!input.id;
    const id = input.id ?? doc(this.fs.col(COL.clients)).id;
    const ref = this.fs.docRef<Client>(COL.clients, id);

    let existing: Client | null = null;
    if (isExistingDoc) {
      const snap = await getDoc(ref);
      existing = snap.exists() ? ({ id: snap.id, ...snap.data() } as Client) : null;
    }

    const isCompany = (input.personType ?? null) === 2;
    const personName = isCompany
      ? input.companyName ?? input.name ?? ""
      : input.name ?? "";
    const documentId = input.documentId
      ? String(input.documentId).replace(/\D/g, "")
      : null;
    const birthdate = input.birthdate
      ? (Timestamp.fromDate(input.birthdate) as Timestamp)
      : null;
    const personType = input.personType ?? null;

    const now = serverTimestamp() as unknown as Timestamp;
    const companyName = input.companyName;
    const companyNameLower = (companyName || "").toLowerCase();

    const body: any = {
      creatorUid: creator.uid,
      creatorName: creator.name,

      companyName,
      companyNameLower,
      agent: input.agent ?? null,
      category: input.category ?? null,
      additionalInfo: input.additionalInfo ?? null,

      name: personName,
      lastname: isCompany ? null : input.lastname ?? null,
      documentId,
      birthdate,
      personType,

      phoneNumber: input.phoneNumber ?? null,
      email: input.email ?? null,
      address: input.description_address ?? null,

      updatedAt: now,
    };

    const isNew = !existing;
    if (isNew) {
      body.totalBalance = 0;
      body.unpaidCount = 0;
      body.createdAt = now;
    }

    await setDoc(ref, body, { merge: true });

    // Build the response locally — no second getDoc.
    const localNow = Timestamp.now();
    return {
      id,
      creatorUid: creator.uid,
      creatorName: creator.name,
      companyName,
      companyNameLower,
      agent: input.agent ?? null,
      category: input.category ?? null,
      additionalInfo: input.additionalInfo ?? null,
      name: personName,
      lastname: isCompany ? null : input.lastname ?? null,
      documentId,
      birthdate,
      personType,
      phoneNumber: input.phoneNumber ?? null,
      email: input.email ?? null,
      address: input.description_address ?? null,
      totalBalance: existing?.totalBalance ?? 0,
      unpaidCount: existing?.unpaidCount ?? 0,
      createdAt: existing?.createdAt ?? localNow,
      updatedAt: localNow,
    };
  }
}
